import { AlertTriangle, MapPin, Phone, Plus } from 'lucide-react';
import { useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { FormInput, FormSelect, FormTextArea } from '../forms';
import { useNavigate } from "react-router-dom";

export const SafetyContract = ({ sessionId, clientId, onComplete, initialData = {} }) => {
  const { put } = useApi();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Safety Assessment
    riskLevel: initialData?.safety?.riskLevel || 'none',
    concerns: initialData?.safety?.concerns || '',
    safetyPlan: initialData?.safety?.safetyPlan || '',
    safetyContractRequired: true,

    // Contract Details
    startDate: initialData?.safetyContract?.startDate || new Date().toISOString().split('T')[0],
    endDate: initialData?.safetyContract?.endDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    selfCareActions: initialData?.safetyContract?.selfCareActions || [],
    emergencyContacts: initialData?.safetyContract?.emergencyContacts || [],
    safetyPlaces: initialData?.safetyContract?.safetyPlaces || [],
    agreementText: initialData?.safetyContract?.agreementText || '',
    signed: initialData?.safetyContract?.signed || false,
    signedAt: initialData?.safetyContract?.signedAt || null,
    witnessName: initialData?.safetyContract?.witnessName || '',
    witnessSignature: initialData?.safetyContract?.witnessSignature || false
  });

  const [currentAction, setCurrentAction] = useState('');
  const [newContact, setNewContact] = useState({ name: '', contact: '', relation: '' });
  const [newPlace, setNewPlace] = useState({ name: '', location: '', contact: '' });
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    // Validation: Ensure at least one emergency contact is provided
    if (formData.emergencyContacts.length === 0) {
      setErrors({ emergencyContacts: "At least one emergency contact is required." });
      setLoading(false);
      return;
    }

    try {
      const payload = {
        safety: {
          riskLevel: formData.riskLevel,
          concerns: formData.concerns,
          safetyPlan: formData.safetyPlan,
          safetyContractRequired: true
        },
        safetyContract: {
          startDate: formData.startDate,
          endDate: formData.endDate,
          selfCareActions: formData.selfCareActions,
          emergencyContacts: formData.emergencyContacts,
          safetyPlaces: formData.safetyPlaces,
          agreementText: formData.agreementText,
          signed: formData.signed,
          signedAt: formData.signedAt,
          witnessName: formData.witnessName,
          witnessSignature: formData.witnessSignature,
          completedAt: formData.signed && formData.witnessSignature ? new Date().toISOString() : null
        }
      };

      const response = await put(`/sessions/${sessionId}/safety`, payload);

      if (response.data?.status === 'success') {
        // Critical: Call onComplete with the full response data
        alert("✅ Session data saved successfully. Redirecting...");
        navigate("/counselor/sessions");
      }
    } catch (error) {
      console.error('Safety contract error:', error);
      setErrors(error.response?.data?.errors || {
        general: 'Failed to save safety assessment and contract'
      });
    } finally {
      setLoading(false);
    }
  };


  const addSelfCareAction = () => {
    if (currentAction.trim()) {
      setFormData(prev => ({
        ...prev,
        selfCareActions: [...prev.selfCareActions, currentAction.trim()]
      }));
      setCurrentAction('');
    }
  };

  const addEmergencyContact = () => {
    if (newContact.name && newContact.contact) {
      setFormData(prev => ({
        ...prev,
        emergencyContacts: [...prev.emergencyContacts, { ...newContact }]
      }));
      setNewContact({ name: '', contact: '', relation: '' });
    }
  };

  const addSafetyPlace = () => {
    if (newPlace.name && newPlace.location) {
      setFormData(prev => ({
        ...prev,
        safetyPlaces: [...prev.safetyPlaces, { ...newPlace }]
      }));
      setNewPlace({ name: '', location: '', contact: '' });
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-medium text-gray-900">Safety Contract</h2>
      </div>

      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <p className="text-sm text-red-700">
              This is a formal agreement for safety. Please ensure all information is accurate and understood by the client.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Risk Assessment Section */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-900">Risk Assessment</h3>
          <FormSelect
            label="Risk Level"
            value={formData.riskLevel}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              riskLevel: e.target.value
            }))}
            options={[
              { value: 'none', label: 'No Risk' },
              { value: 'low', label: 'Low Risk' },
              { value: 'moderate', label: 'Moderate Risk' },
              { value: 'high', label: 'High Risk' },
              { value: 'severe', label: 'Severe Risk' }
            ]}
            required
            error={errors.riskLevel}
          />

          <FormTextArea
            label="Safety Concerns"
            value={formData.concerns}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              concerns: e.target.value
            }))}
            placeholder="Describe any safety concerns or risk factors..."
            required
            error={errors.concerns}
          />

          <FormTextArea
            label="Safety Plan"
            value={formData.safetyPlan}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              safetyPlan: e.target.value
            }))}
            placeholder="Outline the safety plan and interventions..."
            required
            error={errors.safetyPlan}
          />
        </div>

        {/* Contract Period */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            type="date"
            label="Start Date"
            value={formData.startDate}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              startDate: e.target.value
            }))}
            error={errors.startDate}
            required
          />
          <FormInput
            type="date"
            label="End Date"
            value={formData.endDate}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              endDate: e.target.value
            }))}
            error={errors.endDate}
            required
          />
        </div>

        {/* Self-Care Actions */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-900">Self-Care Actions</h3>
          <div className="flex space-x-2">
            <FormInput
              value={currentAction}
              onChange={(e) => setCurrentAction(e.target.value)}
              placeholder="Enter a self-care action"
              className="flex-1"
            />
            <button
              type="button"
              onClick={addSelfCareAction}
              className="px-4 py-2 bg-uzima-maroon-500 text-white rounded-md hover:bg-uzima-maroon-600"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-2">
            {formData.selfCareActions.map((action, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                <span>{action}</span>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    selfCareActions: prev.selfCareActions.filter((_, i) => i !== index)
                  }))}
                  className="text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Emergency Contacts */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-900">Emergency Contacts</h3>
          <div className="space-y-3">
            <FormInput
              placeholder="Name"
              value={newContact.name}
              onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
              required
            />
            <FormInput
              placeholder="Phone Number"
              value={newContact.contact}
              onChange={(e) => setNewContact(prev => ({ ...prev, contact: e.target.value }))}
              required
            />
            <FormInput
              placeholder="Relation"
              value={newContact.relation}
              onChange={(e) => setNewContact(prev => ({ ...prev, relation: e.target.value }))}
              required
            />
            <button
              type="button"
              onClick={addEmergencyContact}
              className="w-full px-4 py-2 bg-uzima-maroon-500 text-white rounded-md hover:bg-uzima-maroon-600"
            >
              Add Emergency Contact
            </button>
            {/* Display error if emergencyContacts is empty */}
            {errors.emergencyContacts && (
              <p className="text-sm text-red-600">{errors.emergencyContacts}</p>
            )}
          </div>
          <div className="space-y-2">
            {formData.emergencyContacts.map((contact, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{contact.name}</p>
                    <p className="text-sm text-gray-500">{contact.relation}</p>
                    <p className="text-sm text-gray-500">{contact.contact}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      emergencyContacts: prev.emergencyContacts.filter((_, i) => i !== index)
                    }))}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Safety Places */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-900">Safe Places to Go</h3>
          <div className="space-y-3">
            <FormInput
              placeholder="Place Name (e.g., Local Counseling Center)"
              value={newPlace.name}
              onChange={(e) => setNewPlace(prev => ({ ...prev, name: e.target.value }))}
            />
            <FormInput
              placeholder="Location"
              value={newPlace.location}
              onChange={(e) => setNewPlace(prev => ({ ...prev, location: e.target.value }))}
            />
            <FormInput
              placeholder="Contact Number"
              value={newPlace.contact}
              onChange={(e) => setNewPlace(prev => ({ ...prev, contact: e.target.value }))}
            />
            <button
              type="button"
              onClick={addSafetyPlace}
              className="w-full px-4 py-2 bg-uzima-maroon-500 text-white rounded-md hover:bg-uzima-maroon-600"
            >
              Add Safe Place
            </button>
          </div>
          <div className="space-y-2">
            {formData.safetyPlaces.map((place, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{place.name}</p>
                    <p className="text-sm text-gray-500 flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      {place.location}
                    </p>
                    <p className="text-sm text-gray-500 flex items-center">
                      <Phone className="h-4 w-4 mr-1" />
                      {place.contact}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      safetyPlaces: prev.safetyPlaces.filter((_, i) => i !== index)
                    }))}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Agreement Text */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-900">Agreement</h3>
          <div className="bg-gray-50 p-4 rounded">
            <p className="text-sm text-gray-700">
              I agree to NOT harm myself in any way, attempt to kill myself, or kill myself
              during the period specified above. I agree that if the appointment session is
              postponed, cancelled, etc., that this time period is extended until the next
              direct meeting with my counselor.
            </p>
          </div>
          <FormTextArea
            label="Additional Agreement Terms"
            value={formData.agreementText}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              agreementText: e.target.value
            }))}
            placeholder="Add any additional terms or notes to the agreement..."
          />
        </div>

        {/* Signatures */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-900">Signatures</h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="clientSignature"
                checked={formData.signed}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  signed: e.target.checked,
                  signedAt: e.target.checked ? new Date().toISOString() : null
                }))}
                className="h-4 w-4 text-uzima-maroon-500"
                required
              />
              <label htmlFor="clientSignature" className="text-sm text-gray-700">
                I understand and agree to the terms of this safety contract
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Witness Name"
                value={formData.witnessName}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  witnessName: e.target.value
                }))}
                required
              />
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="witnessSignature"
                  checked={formData.witnessSignature}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    witnessSignature: e.target.checked
                  }))}
                  className="h-4 w-4 text-uzima-maroon-500"
                  required
                />
                <label htmlFor="witnessSignature" className="text-sm text-gray-700">
                  Witness Signature
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {errors.general && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <p className="ml-3 text-sm text-red-700">{errors.general}</p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end pt-6">
          <button
            type="submit"
            disabled={loading || !formData.signed}
            className="flex items-center px-6 py-2 bg-uzima-maroon-500 text-white rounded-md hover:bg-uzima-maroon-600 disabled:bg-gray-300"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Saving...
              </>
            ) : (
              'Complete Safety Contract'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SafetyContract;